'use client';

import './globals.css';
import { useEffect } from 'react';
import { geistMono, geistSans } from './fonts';
import { RenderErrorView } from '@/components/render-error-view';
import { reloadIfChunkLoadError } from '@/hooks/use-version-skew';
import { reportRenderError } from '@/lib/report-render-error';

// Last resort: a throw in the root layout (or in app/error.tsx itself) replaces
// the whole document, so this boundary supplies its own <html>/<body>, the
// stylesheet the layout would have carried, and the font variables globals.css
// resolves --font-sans through.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Report before handing over: the reload is how a stale-chunk tab recovers,
    // and /admin/telemetry/client is the only place we can measure how often a
    // deploy strands someone. The queue's pagehide flush carries it out.
    reportRenderError(error, 'app/global-error');
    // A tab whose chunk the last deploy deleted cannot be fixed by reset() —
    // the chunk is still gone, so the retry can only re-throw. Reloading is
    // safe in THIS boundary and only this one: it replaces the document, so
    // the root layout's softphone and pool providers are already gone and
    // there is no parked line left to release first. When the seam declines
    // (offline, or already reloaded once) this is an ordinary failure and the
    // view below is the right answer.
    reloadIfChunkLoadError(error);
  }, [error]);

  return (
    <html lang="en" className={`${geistSans.variable} ${geistMono.variable}`}>
      <body className="min-h-screen antialiased">
        <RenderErrorView
          title="The CRM could not start"
          message="Something failed while loading the app. Try again — if it keeps failing, reload the page."
          digest={error.digest}
          onRetry={reset}
          home={{ href: '/', label: 'Go to dashboard' }}
          hardNav
        />
      </body>
    </html>
  );
}
